/**
 * services/BalanceHistoryService.ts
 * Service for managing balance history transactions with automatic balance recalculation
 *
 * Maintains a chronological ledger of all balance changes for trading accounts.
 * Each transaction records the amount change and the resulting balance after.
 * When transactions are added, updated, or deleted, all subsequent balances
 * are automatically recalculated to maintain consistency.
 *
 * Key features:
 * - Chronological queries using compound index [accountId+timestamp]
 * - Automatic balanceAfter calculation on transaction insert
 * - Cascade recalculation when transactions are modified or deleted
 * - Support for backdated transaction insertion
 * - Negative balances allowed (margin accounts, prop firm drawdowns)
 * - All mutating operations are atomic (insert/update/delete + recalculation in single transaction)
 */

import Dexie from 'dexie';

import { BaseService } from './BaseService';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import { db } from '@/lib/db';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { generateUUID } from '@/utils/uuid';
import { SYNC_TABLES, type SyncTableName } from '@/constants/syncTables';
import { getSyncOperationService } from './sync/SyncOperationService';
import { buildSetOps, buildDiffOps } from './sync/OperationBuilder';
import { softDeleteRecord } from './sync/SoftDeleteUtils';
import { syncCoordinatorService } from './sync/SyncCoordinatorService';

/**
 * Service for managing balance history transactions
 *
 * Provides CRUD operations plus chronological queries and automatic balance
 * recalculation. Inherits get(), getAll() from BaseService.
 *
 * Note: This service overrides add(), update(), and softDelete() to implement
 * custom balance recalculation logic while maintaining sync op logging.
 */
export class BalanceHistoryService extends BaseService<BalanceHistory> {
  constructor() {
    super('balance_histories');
  }

  // ===== PRIVATE HELPERS =====

  /**
   * Check if a record is tombstoned (soft-deleted)
   *
   * @param record - The balance history record to check
   * @returns true if the record is tombstoned
   */
  private isTombstoned(record: BalanceHistory): boolean {
    return typeof record.deletedAt === 'string' && record.deletedAt.length > 0;
  }

  /**
   * Sort records by timestamp with deterministic secondary sort
   *
   * When timestamps are equal, sorts by createdAt, then by id for stability.
   *
   * @param records - Array of balance history records
   * @returns Sorted array (ascending by timestamp)
   */
  private sortByTimestamp(records: BalanceHistory[]): BalanceHistory[] {
    return [...records].sort((a, b) => {
      // Primary sort: timestamp ascending
      const timestampCompare = a.timestamp.localeCompare(b.timestamp);
      if (timestampCompare !== 0) return timestampCompare;

      // Secondary sort: createdAt ascending
      const createdAtCompare = a.createdAt.localeCompare(b.createdAt);
      if (createdAtCompare !== 0) return createdAtCompare;

      // Tertiary sort: id ascending (guaranteed unique)
      return a.id.localeCompare(b.id);
    });
  }

  /**
   * Compare two records using the deterministic sort order
   *
   * Returns negative if a comes before b, positive if a comes after b, 0 if equal.
   *
   * @param a - First record
   * @param b - Second record
   * @returns Comparison result
   */
  private compareByTimestamp(a: BalanceHistory, b: BalanceHistory): number {
    const timestampCompare = a.timestamp.localeCompare(b.timestamp);
    if (timestampCompare !== 0) return timestampCompare;

    const createdAtCompare = a.createdAt.localeCompare(b.createdAt);
    if (createdAtCompare !== 0) return createdAtCompare;

    return a.id.localeCompare(b.id);
  }

  /**
   * Get all transactions for an account using compound index
   *
   * Returns records sorted by timestamp ascending. Includes tombstoned records
   * for internal use (recalculation needs to skip them but know their position).
   *
   * @param accountId - The account ID
   * @returns All transactions including tombstoned ones, sorted by timestamp
   */
  private async getAllTransactionsForAccount(
    accountId: string
  ): Promise<BalanceHistory[]> {
    const records = await this.table
      .where('[accountId+timestamp]')
      .between(
        [accountId, Dexie.minKey],
        [accountId, Dexie.maxKey]
      )
      .toArray();

    return this.sortByTimestamp(records);
  }

  /**
   * Internal recalculation logic without transaction wrapper
   *
   * This method performs the actual balance recalculation and can be called
   * from within an existing Dexie transaction. Does NOT call markAsInstrumentedWriteContext()
   * - the caller is responsible for setting up the transaction context.
   *
   * Note: When multiple transactions share the same timestamp, this method uses
   * deterministic secondary sorting (createdAt, then id) to ensure consistent ordering.
   * The fromTimestamp filter uses simple string comparison, so it may include other
   * records with the same timestamp. This is intentional and correct - the deterministic
   * sort order ensures balances are calculated in the proper sequence regardless of
   * how many same-timestamp records are processed.
   *
   * @param accountId - The account ID
   * @param fromTimestamp - Recalculate from this timestamp forward (inclusive)
   */
  private async _recalculateBalancesInternal(
    accountId: string,
    fromTimestamp: string
  ): Promise<void> {
    // Get all transactions for this account (including tombstoned for ordering context)
    const allTransactions = await this.getAllTransactionsForAccount(accountId);

    // Find the starting balance (balance of last non-tombstoned transaction before fromTimestamp)
    let startingBalance = 0;
    for (const tx of allTransactions) {
      if (tx.timestamp >= fromTimestamp) break;
      if (!this.isTombstoned(tx)) {
        startingBalance = tx.balanceAfter;
      }
    }

    // Get transactions from fromTimestamp forward (non-tombstoned only)
    const transactionsToRecalc = allTransactions.filter(
      tx => tx.timestamp >= fromTimestamp && !this.isTombstoned(tx)
    );

    if (transactionsToRecalc.length === 0) {
      return; // Nothing to recalculate
    }

    // Calculate new balances
    const updates: Array<{ id: string; balanceAfter: number }> = [];
    let runningBalance = startingBalance;

    for (const tx of transactionsToRecalc) {
      runningBalance += tx.amount;
      if (tx.balanceAfter !== runningBalance) {
        updates.push({ id: tx.id, balanceAfter: runningBalance });
      }
    }

    if (updates.length === 0) {
      return; // All balances already correct
    }

    // Perform batch updates (caller provides transaction context)
    const now = new Date().toISOString();

    for (const update of updates) {
      // Get existing record for diff-based op logging
      const existing = await this.table.get(update.id);
      if (!existing) continue;

      // Update the record
      await this.table.update(update.id, {
        balanceAfter: update.balanceAfter,
        updatedAt: now,
      });

      // Log ops for sync
      if (SYNC_TABLES.includes(this.tableName as SyncTableName)) {
        const ops = buildDiffOps(
          this.tableName as SyncTableName,
          update.id,
          existing as unknown as Record<string, unknown>,
          { balanceAfter: update.balanceAfter } as unknown as Record<string, unknown>
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      }
    }
  }

  // ===== PUBLIC QUERY METHODS =====

  /**
   * Get all transactions for an account sorted by timestamp ascending
   *
   * Uses compound index for efficient chronological queries.
   * Excludes tombstoned (soft-deleted) records.
   *
   * @param accountId - The account ID to filter by
   * @returns Array of transactions sorted by timestamp ascending
   */
  async getByAccountId(accountId: string): Promise<BalanceHistory[]> {
    const allRecords = await this.getAllTransactionsForAccount(accountId);
    return allRecords.filter(record => !this.isTombstoned(record));
  }

  /**
   * Get transactions for an account within a date range
   *
   * Uses compound index with inclusive range on both ends.
   * Dates should be ISO strings (e.g., "2024-01-15T00:00:00.000Z").
   *
   * @param accountId - The account ID to filter by
   * @param startDate - Start of range (inclusive), ISO string
   * @param endDate - End of range (inclusive), ISO string
   * @returns Array of transactions within range, sorted by timestamp ascending
   */
  async getByAccountIdAndDateRange(
    accountId: string,
    startDate: string,
    endDate: string
  ): Promise<BalanceHistory[]> {
    const records = await this.table
      .where('[accountId+timestamp]')
      .between(
        [accountId, startDate],
        [accountId, endDate],
        true, // includeLower
        true  // includeUpper
      )
      .toArray();

    const sorted = this.sortByTimestamp(records);
    return sorted.filter(record => !this.isTombstoned(record));
  }

  /**
   * Get transactions linked to a specific trade execution
   *
   * Uses tradeExecutionId index for efficient lookup.
   * Excludes tombstoned records.
   *
   * @param tradeExecutionId - The trade execution ID to filter by
   * @returns Array of transactions linked to this trade
   */
  async getByTradeExecutionId(
    tradeExecutionId: string
  ): Promise<BalanceHistory[]> {
    const records = await this.table
      .where('tradeExecutionId')
      .equals(tradeExecutionId)
      .toArray();

    const sorted = this.sortByTimestamp(records);
    return sorted.filter(record => !this.isTombstoned(record));
  }

  /**
   * Get all balance rows caused by a specific TradeExecutionEvent.
   *
   * Uses the sourceExecutionEventId index. Excludes tombstoned records.
   * Typically returns one row per financial-impact event (entry-fill, partial-exit,
   * final-exit) plus an optional fee row for the same event.
   *
   * @param sourceExecutionEventId - The TradeExecutionEvent ID to look up
   * @returns Balance rows linked to this event, sorted by timestamp ascending
   */
  async getBySourceExecutionEventId(
    sourceExecutionEventId: string
  ): Promise<BalanceHistory[]> {
    const records = await this.table
      .where('sourceExecutionEventId')
      .equals(sourceExecutionEventId)
      .toArray();

    const sorted = this.sortByTimestamp(records);
    return sorted.filter(record => !this.isTombstoned(record));
  }

  /**
   * Get the most recent transaction for an account (current balance)
   *
   * Returns the transaction with the latest timestamp for the account.
   * Returns undefined if no transactions exist for the account.
   *
   * @param accountId - The account ID
   * @returns Most recent transaction, or undefined if none exist
   */
  async getLatestBalance(accountId: string): Promise<BalanceHistory | undefined> {
    const allRecords = await this.getByAccountId(accountId);
    if (allRecords.length === 0) {
      return undefined;
    }
    // Records are already sorted ascending, so last one is most recent
    return allRecords[allRecords.length - 1];
  }

  // ===== BALANCE RECALCULATION =====

  /**
   * Recalculate balanceAfter for all transactions from a given timestamp forward
   *
   * This is the public method that wraps _recalculateBalancesInternal in a transaction.
   * Use this for standalone recalculation. For atomic operations (addTransaction, update,
   * softDelete), the internal method is called directly within their transactions.
   *
   * @param accountId - The account ID
   * @param fromTimestamp - Recalculate from this timestamp forward (inclusive)
   */
  async recalculateBalancesFrom(
    accountId: string,
    fromTimestamp: string
  ): Promise<void> {
    await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
      markAsInstrumentedWriteContext();
      await this._recalculateBalancesInternal(accountId, fromTimestamp);
    });
  }

  // ===== TRANSACTION MANAGEMENT =====

  /**
   * Add a new transaction with automatic balance calculation
   *
   * Calculates balanceAfter based on the previous transaction's balance.
   * If this is the first transaction for the account, balanceAfter equals amount.
   * If the transaction is backdated, recalculates all subsequent transactions.
   *
   * The insert and any cascade recalculation are performed atomically in a single
   * Dexie transaction.
   *
   * @param data - Partial transaction data (timestamp, amount, etc. required)
   * @returns The created transaction with calculated balanceAfter
   */
  async addTransaction(data: Partial<BalanceHistory>): Promise<BalanceHistory> {
    if (!data.timestamp) {
      throw new Error('Transaction timestamp is required');
    }
    if (data.amount === undefined) {
      throw new Error('Transaction amount is required');
    }
    if (!data.accountId) {
      throw new Error('Transaction accountId is required');
    }
    if (!data.transactionType) {
      throw new Error('Transaction transactionType is required');
    }

    const accountId = data.accountId;
    const amountPrecision = data.amountPrecision ?? 2;
    const balancePrecision = data.balancePrecision ?? amountPrecision;

    // Get all existing transactions to find the balance before this timestamp
    const allTransactions = await this.getAllTransactionsForAccount(accountId);
    const nonTombstoned = allTransactions.filter(tx => !this.isTombstoned(tx));

    // Create the new entity to determine its sort position
    const now = new Date().toISOString();
    const entity: BalanceHistory = {
      ...data,
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
      amountPrecision,
      balancePrecision,
      balanceAfter: 0, // Will be calculated below
    } as BalanceHistory;

    // Find previous balance using deterministic sort order
    // Find all transactions that come BEFORE this new one in sort order
    let previousBalance = 0;
    let insertionIndex = 0;

    for (let i = 0; i < nonTombstoned.length; i++) {
      const tx = nonTombstoned[i];
      if (tx && this.compareByTimestamp(tx, entity) < 0) {
        // tx comes before entity
        previousBalance = tx.balanceAfter;
        insertionIndex = i + 1;
      }
    }

    // Calculate balanceAfter for this transaction
    entity.balanceAfter = previousBalance + data.amount;

    // Check if there are subsequent transactions (using sort order, not just timestamp)
    const hasSubsequentTransactions = insertionIndex < nonTombstoned.length;
    const firstSubsequentTimestamp = hasSubsequentTransactions
      ? nonTombstoned[insertionIndex]?.timestamp
      : undefined;

    // Insert and recalculate atomically in single transaction
    await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
      markAsInstrumentedWriteContext();

      // Insert the new transaction
      await this.table.add(entity);

      // Log ops for sync
      if (SYNC_TABLES.includes(this.tableName as SyncTableName)) {
        const ops = buildSetOps(
          this.tableName as SyncTableName,
          entity.id,
          entity as unknown as Record<string, unknown>
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      }

      // If there are subsequent transactions, recalculate their balances atomically
      if (hasSubsequentTransactions && firstSubsequentTimestamp) {
        await this._recalculateBalancesInternal(accountId, firstSubsequentTimestamp);
      }
    });

    return entity;
  }

  /**
   * Add a balance row driven by a TradeExecutionEvent (§8.4 step 3).
   *
   * IMPORTANT: Must be called inside an active Dexie 'rw' transaction that
   * includes db.balance_histories, db.sync_operations, and db.sync_state.
   * Dexie auto-propagates the active transaction — all reads and writes below
   * participate in the caller's transaction without opening a nested one.
   *
   * `sourceExecutionEventId` is required and enforced by this method's signature.
   * Use addTransaction() for non-execution-driven rows (deposits, withdrawals, etc.).
   *
   * Balance calculation and cascade recalculation follow the same logic as
   * addTransaction() — insertion position is determined by deterministic timestamp
   * sort, and all subsequent rows are recalculated if the new row is backdated.
   *
   * @param data - Transaction fields including required sourceExecutionEventId
   * @returns The created BalanceHistory record
   */
  async addExecutionTransaction(
    data: {
      accountId: string;
      timestamp: string;
      transactionType: BalanceHistory['transactionType'];
      amount: number;
      /** Required canonical linkage to the triggering TradeExecutionEvent */
      sourceExecutionEventId: string;
      /** Optional deprecated linkage to the TradeExecution container */
      tradeExecutionId?: string;
      description?: string;
      amountPrecision?: number;
      balancePrecision?: number;
    }
  ): Promise<BalanceHistory> {
    const { accountId } = data;
    const amountPrecision = data.amountPrecision ?? 2;
    const balancePrecision = data.balancePrecision ?? amountPrecision;

    // Read all existing transactions within the active transaction so that
    // any writes from earlier steps (event append, projection update) are visible.
    const allTransactions = await this.getAllTransactionsForAccount(accountId);
    const nonTombstoned = allTransactions.filter(tx => !this.isTombstoned(tx));

    // Build entity (balanceAfter calculated below)
    const now = new Date().toISOString();
    const entity: BalanceHistory = {
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
      accountId,
      timestamp: data.timestamp,
      transactionType: data.transactionType,
      amount: data.amount,
      amountPrecision,
      balancePrecision,
      balanceAfter: 0, // set below
      sourceExecutionEventId: data.sourceExecutionEventId,
      ...(data.tradeExecutionId !== undefined && { tradeExecutionId: data.tradeExecutionId }),
      ...(data.description !== undefined && { description: data.description }),
    };

    // Determine insertion position using deterministic sort order
    let previousBalance = 0;
    let insertionIndex = 0;
    for (let i = 0; i < nonTombstoned.length; i++) {
      const tx = nonTombstoned[i];
      if (tx && this.compareByTimestamp(tx, entity) < 0) {
        previousBalance = tx.balanceAfter;
        insertionIndex = i + 1;
      }
    }

    entity.balanceAfter = previousBalance + data.amount;

    const hasSubsequentTransactions = insertionIndex < nonTombstoned.length;
    const firstSubsequentTimestamp = hasSubsequentTransactions
      ? nonTombstoned[insertionIndex]?.timestamp
      : undefined;

    // Write within the caller's active transaction
    markAsInstrumentedWriteContext();
    await this.table.add(entity);

    // Log set ops for sync
    if (SYNC_TABLES.includes(this.tableName as SyncTableName)) {
      const ops = buildSetOps(
        this.tableName as SyncTableName,
        entity.id,
        entity as unknown as Record<string, unknown>
      );
      if (ops.length > 0) {
        await getSyncOperationService().logBatch(ops);
      }
    }

    // Cascade recalculation for subsequent rows (within the same transaction)
    if (hasSubsequentTransactions && firstSubsequentTimestamp) {
      await this._recalculateBalancesInternal(accountId, firstSubsequentTimestamp);
    }

    return entity;
  }

  /**
   * Update a transaction and recalculate affected balances
   *
   * The update and recalculation are performed atomically in a single Dexie transaction.
   *
   * @param id - Transaction ID to update
   * @param changes - Partial data to update
   * @returns Updated transaction
   */
  override async update(
    id: string,
    changes: Partial<BalanceHistory>
  ): Promise<BalanceHistory> {
    // Get the existing transaction to find timestamp and accountId
    const existing = await this.get(id);
    if (!existing) {
      throw new Error(`BalanceHistory with id ${id} not found`);
    }

    const accountId = existing.accountId;
    // Use the earlier of old and new timestamp for recalculation start
    const oldTimestamp = existing.timestamp;
    const newTimestamp = changes.timestamp ?? oldTimestamp;
    const recalcFromTimestamp = oldTimestamp < newTimestamp ? oldTimestamp : newTimestamp;

    // Perform update
    const updatedData = {
      ...changes,
      updatedAt: new Date().toISOString(),
    };

    let updatedEntity: BalanceHistory | undefined;

    // Update and recalculate atomically in single transaction
    await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
      markAsInstrumentedWriteContext();

      // Update the record
      await this.table.update(id, updatedData as Parameters<typeof this.table.update>[1]);

      // Log ops for sync (diff-based)
      if (SYNC_TABLES.includes(this.tableName as SyncTableName)) {
        const ops = buildDiffOps(
          this.tableName as SyncTableName,
          id,
          existing as unknown as Record<string, unknown>,
          changes as unknown as Record<string, unknown>
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      }

      updatedEntity = await this.table.get(id);

      // Recalculate balances atomically within same transaction
      await this._recalculateBalancesInternal(accountId, recalcFromTimestamp);
    });

    if (!updatedEntity) {
      throw new Error(`Failed to retrieve updated BalanceHistory`);
    }

    // Re-fetch after recalculation to get correct balanceAfter
    const finalEntity = await this.get(id);
    return finalEntity ?? updatedEntity;
  }

  /**
   * Soft-delete a transaction and recalculate affected balances
   *
   * The soft-delete and recalculation are performed atomically in a single Dexie transaction.
   *
   * @param id - Transaction ID to soft-delete
   */
  override async softDelete(
    this: BalanceHistoryService,
    id: string
  ): Promise<void> {
    // Get the existing transaction to find timestamp and accountId
    const existing = await this.get(id);
    if (!existing) {
      throw new Error(`BalanceHistory with id ${id} not found`);
    }

    // Check if already tombstoned (idempotent)
    if (this.isTombstoned(existing)) {
      return;
    }

    const accountId = existing.accountId;
    const timestamp = existing.timestamp;

    // Soft-delete and recalculate atomically in single transaction
    await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
      markAsInstrumentedWriteContext();

      // Delegate to softDeleteRecord for tombstone logic
      const result = await softDeleteRecord(
        this.tableName as SyncTableName,
        id,
        { notify: false }
      );

      // Log delete op for sync
      if (result.deleted && SYNC_TABLES.includes(this.tableName as SyncTableName)) {
        await getSyncOperationService().logOperation({
          table: this.tableName as SyncTableName,
          recordId: id,
          op: 'delete',
        });
      }

      // Recalculate balances atomically within same transaction
      await this._recalculateBalancesInternal(accountId, timestamp);
    });

    // Notify sync coordinator (after transaction commits)
    syncCoordinatorService.notifyChange(this.tableName, id);
  }
}

// Export singleton instance
export const balanceHistoryService = new BalanceHistoryService();
